import { useCallback, useId } from 'react';
import { useModalContext } from './ModalContext';
import type { ComponentType } from 'react';
import type { ModalComponentProps, UseModalReturn } from './ModalContext.types';

/**
 * Custom hook that provides an abstraction layer over the modal context.
 * Takes a component type and returns functions to open/close that specific modal.
 * Uses React's useId hook to generate a unique identifier for the modal instance.
 * 
 * @template T - The props type for the modal component (must extend ModalComponentProps)
 * @param Component - React component to use for the modal
 * @returns An object containing openModal, closeModal functions and the modalId
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const confirmationModal = useModal(ConfirmationModal);
 *   
 *   const handleDelete = () => {
 *     confirmationModal.openModal({
 *       title: 'Delete Item',
 *       message: 'Are you sure you want to delete this item?',
 *       show: true,
 *       onConfirm: () => {
 *         // perform delete
 *         confirmationModal.closeModal();
 *       },
 *       onCancel: confirmationModal.closeModal
 *     });
 *   };
 *   
 *   return <button onClick={handleDelete}>Delete</button>;
 * }
 * ```
 */
export const useModal = <T extends ModalComponentProps>(
  Component: ComponentType<T>
): UseModalReturn<T> => {
  const { openModal: contextOpenModal, closeModal: contextCloseModal, updateModal: contextUpdateModal } = useModalContext();
  const modalId = useId();

  const openModal = useCallback((props: T) => {
    contextOpenModal(modalId, Component, props);
  }, [contextOpenModal, Component, modalId]);

  const closeModal = useCallback(() => {
    contextCloseModal(modalId);
  }, [contextCloseModal, modalId]);

  const updateModal = useCallback((newProps: Partial<T>) => {
    contextUpdateModal(modalId, newProps);
  }, [contextUpdateModal, modalId]);

  return {
    openModal,
    closeModal,
    modalId,
    updateModal,
  };
};